import {ApiResponse} from "apisauce";
import {http} from "./api/restApiClient";
import {Page} from "./api/pagination";

export type BrandingThemeColors = {
  primary: string;
  backgroundDefault: string;
  backgroundLight: string;
  fillSuccess: string;
  textSuccess: string;
  fillInfo: string;
  textInfo: string;
  textPrimary: string;
};

export type CustomThemeModel = {
  id: string;
  organization_id: string;
  theme_name: string;
  logo: string | File;
  logo_filename: string;
  logo_type: string;
  logo_size: number;
  colors: BrandingThemeColors;
  active: boolean;
  created: string;
  created_by: string;
};

export type CreateThemeRequest = Pick<
  CustomThemeModel,
  "theme_name" | "logo" | "colors"
>;

/** Convert File to base64 string with proper data URI format */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

/** Convert CreateThemeRequest to JSON payload that mimics multipart/form-data structure */
const getJsonPayload = async (data: CreateThemeRequest) => {
  const payload: Record<string, any> = {
    theme_name: data.theme_name,
    colors: data.colors,
  };

  if (data.logo instanceof File) {
    const base64 = await fileToBase64(data.logo);
    payload.logo = base64;
    payload.logo_filename = data.logo.name;
    payload.logo_size = data.logo.size;
    payload.logo_type = data.logo.type;
  }

  return payload;
};

export const BrandingService = {
  // Custom Branding Theming
  createTheme: async (
    organizationId: string,
    data: CreateThemeRequest,
  ): Promise<ApiResponse<CustomThemeModel>> => {
    const jsonPayload = await getJsonPayload(data);

    return http.post(`/organizations/${organizationId}/themes`, jsonPayload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  },

  updateTheme: async (
    organizationId: string,
    themeId: string,
    data: CreateThemeRequest,
  ) => {
    const jsonPayload = await getJsonPayload(data);

    return http.post(
      `/organizations/${organizationId}/themes/${themeId}`,
      jsonPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  },

  getTheme: (
    organizationId: string,
    themeId: string,
  ): Promise<ApiResponse<CustomThemeModel>> =>
    http.get(`/organizations/${organizationId}/themes/${themeId}`),

  getActiveTheme: (
    organizationId: string,
  ): Promise<ApiResponse<CustomThemeModel>> =>
    http.get(`/organizations/${organizationId}/themes/active`),

  getThemes: (
    organizationId: string,
    params: {query?: string},
  ): Promise<ApiResponse<Page<CustomThemeModel>>> =>
    http.get(`/organizations/${organizationId}/themes`, params),

  /** Get the active theme for a resident on successful verification in case he opened an email admission link. Endpoint is extended to accept ADMISSION token as valid authorization medium.
   *
   * https://vscode.dev/github/Reside-Admissions/core/blob/DEV-2208-customize-resident-experience-with-branding/reside-fe/reside-app/src/utils/token.ts#L4
   *
   */
  getActiveThemeWithAdmissionToken: (
    token: string,
  ): Promise<ApiResponse<Page<CustomThemeModel>>> => {
    return http.get("/workflows/theme", undefined, {
      params: {
        token,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  activateTheme: (organizationId: string, themeId: string) =>
    http.put(`/organizations/${organizationId}/themes/${themeId}/activate`),

  activateDefaultTheme: (organizationId: string) =>
    http.put(`/organizations/${organizationId}/themes/default`),

  deleteTheme: (organizationId: string, themeId: string) =>
    http.delete(`/organizations/${organizationId}/themes/${themeId}`),
};
